import globalScope from '../global-scope.mjs';
import * as vars from './vars.mjs';
import * as util from '../util.mjs';

/** 
 * Logic that supports mutating the display/rendering of post pages. These scripts will want to be 
 * loaded after the DOM. These should be ranked high in terms of load priority, as the UI needs 
 * to be displayed before allowing any interaction. 
 */

/**
 * If triggered by the contents of the page URL, display a top-level message communicating the 
 * result of the user attempting to confirmation their request to subscribe to follow-up 
 * comments for the context post.
 */
export const displayPostSubscribeConfirmMessage = function() {
	const urlHash = window.location.hash;
	if (urlHash.length > 0) {
		const urlHashValue = urlHash.substring(1);
		if (urlHashValue === 'subscribe-confirm-success') {
	    	const postMsgSubscribeConfirmSuccessEle = 
	    		vars.backstageMainEle.querySelector('.post-msg-subscribe-confirm-success');
	    	const siteMsgEle = retrieveDisplayedPostMessageElement();
	    	util.swapNodes(siteMsgEle, postMsgSubscribeConfirmSuccessEle);
		} else if (urlHashValue === 'subscribe-confirm-failure') {
	    	const postMsgSubscribeConfirmFailureEle = 
	    		vars.backstageMainEle.querySelector('.post-msg-subscribe-confirm-failure');
	    	const siteMsgEle = retrieveDisplayedPostMessageElement();
	    	util.swapNodes(siteMsgEle, postMsgSubscribeConfirmFailureEle);
		}
	}
};

/**
 * Display a warning to the user that their browser is unsupported.
 */
export const showBrowserWarning = function() {
	const postMsgDefaultEle = document.querySelector('.site-msg-default');
	const postMsgWarningEle = document.querySelector('.site-msg-browser-unsupported');
	try {
		util.swapNodes(postMsgDefaultEle, postMsgWarningEle);
	} catch (error) {
		/*
		 * IE v9 should be able to execute util.swapNodes(). However, if the browser is even 
		 * older, it may error. In that case, dumb the logic down.
		 */
		console.error(error);
		postMsgDefaultEle.parentNode.insertBefore(postMsgWarningEle, postMsgDefaultEle);
	}
};

/**
 * On mobile, the lead image thumbnail is made invisible by default in order to minimize layout 
 * shift. Undo that.
 */
export const showLeadImage = function() {
	const leadImageBoxEle = document.querySelector('.lead-image-box');
	if (leadImageBoxEle) {
		const stylesheetLinkEle = document.querySelector('link[rel="stylesheet"]');
		let initPendingCnt = 0;
		/*
		 * Might need to wait a beat for the CSS to load and apply styling to the element, as 
		 * the styling (principally float: none) is what causes the layout shift.
		 */
		let setLeadImageVisibility = setInterval(function() {
			try {
				/*
				 * The value of the stylesheet link element's "media" attribute is changed on-load. 
				 * Leverage this to determine if the stylesheet has loaded.
				 *
				 * If initPendingCnt gets too high (possibly because the CSS never loads), stop 
				 * waiting and just display the element.
				 */
				if (!stylesheetLinkEle || stylesheetLinkEle.media === 'all' || initPendingCnt > 10) {
					clearInterval(setLeadImageVisibility);
					leadImageBoxEle.style.visibility = 'visible';
				} else {
					initPendingCnt++;
					console.log('CSS load may still be pending...');
				}
			} catch (error) {
				console.error(error);
				clearInterval(setLeadImageVisibility);
			}
		}, 100);
	}
};

/**
 * The UI, by default, assumes an unsupported browser and hides the comment submission form. 
 * Show it.
 */
export const showCommentForm = function() {
	vars.commentFormBoxEle.setAttribute('aria-hidden', 'false');
};

/**
 * The UI, by default, assumes an unsupported browser and hides the comment reply links. 
 * Show them.
 */
export const showCommentReplyLinks = function() {
	if (vars.commentReplyLinks) {
		vars.commentReplyLinks.forEach(function(commentReplyLinkEle) {
			commentReplyLinkEle.setAttribute('aria-hidden', 'false');
		});
	}
};

/**
 * Instead of providing a mechanism for the user to switch between the possible after-post panes, 
 * just stack all of the panes and display them all at once. Intended for use by unsupported 
 * browsers.
 */
export const stackAfterPostPanes = function() {
	const tabListEle = document.querySelector('#after-post-box [role="tablist"]');
	if (tabListEle) {
		tabListEle.style.display = 'none';
	}

	const commentPaneEle = document.querySelector('#comment-pane');
	if (commentPaneEle && vars.commentsBoxEle) {
		const commentCnt = vars.commentsBoxEle.getAttribute('data-comments-count');
		//console.log('commentCnt = %o', commentCnt);
		if (commentCnt !== '0') {
			commentPaneEle.style.display = 'block';
		} else {
			commentPaneEle.style.display = 'none';
		}
	}

	/*
	 * Mailchimp supports IE v10, but not IE v9. Go ahead and display the subscribe form to both, 
	 * as it isn't clear to me how Mailchimp is distinguishing between them and I don't want to 
	 * unnecessarily exclude IE v10 users.
	 */
	const subscribePane = document.querySelector('#subscribe-pane');
	if (subscribePane) {
		subscribePane.style.display = 'block';
	}
};

/**
 * If, upon page load, the window URL contains a hash that identifies a comment (by ID), scroll 
 * that identified comment into view.
 */
export const displayLinkedToComment = function() {
	const urlHash = window.location.hash;
	if (urlHash.length > 0) {
		const urlHashValue = urlHash.substring(1);
		if (urlHashValue.startsWith('comment-')) {
			const commentEle = vars.commentPaneEle.querySelector('#' + urlHashValue);

			/*
			 * Once the comment pane is active/current, programmatically do what the 
			 * browser would have automatically done (scroll the comment into view).
			 */
			activateAndScrollToCommentsPane(commentEle);
		}
	}
};

/**
 * Activate the comment pane (if not already selected) and bring it into view.
 * @param {DOM.Element} scrollTgtEle - DOM Element that should be scrolled into view after the 
 *	comment pane is activated. This is assumed to be a child of the comment pane.
 */
export const activateAndScrollToCommentsPane = function(scrollTgtEle) {
	const commentTabEle = document.querySelector('#comment-tab');
	/*
	 * In unsupported browsers, the after-post panes are stacked and displayed all at 
	 * once. In this case, no JavaScript is needed.
	 */
	if (commentTabEle !== null) {
		/*
		 * If there are no recommended posts for the current post, the comment pane might 
		 * already be active/current, meaning no JavaScript is needed.
		 */
		if (commentTabEle.getAttribute('aria-selected') === 'false') {
			// Simulate a user-click on the comment tab.
			commentTabEle.click();
		}

		/*
		 * Now that the comment pane is active/current, scroll the comment pane child element
		 * into view.
		 */
		scrollTgtEle.scrollIntoView(true);

		let eleTabbable = false;
		const tabIdxBefore = scrollTgtEle.tabIndex;
		/*
		 * Place focus on the target element. This is primarily to aid in accessibility.
		 * From a screen-reader perspective, the target element having a tabIndex equal to -1 is 
		 * not enough. With that value, the screen-reader will not read out anything on-focus.
		 */
		if (scrollTgtEle.tabIndex !== null && 
			typeof scrollTgtEle.tabIndex !== 'undefined' && 
			scrollTgtEle.tabIndex > -1
		) {
			eleTabbable = true;
		}

		if (!eleTabbable) {
			scrollTgtEle.tabIndex = '0';
		}
		scrollTgtEle.focus();
		if (!eleTabbable) {
			scrollTgtEle.tabIndex = tabIdxBefore;
		}
	}
};

/**
 * The site message box may either be empty (default) or displaying a browser advisement 
 * (warning). Be prepared for either.
 */
const retrieveDisplayedPostMessageElement = function() {
	return document.querySelector('.site-msg-box .site-msg-default, .site-msg-box .site-msg-warning');
};
